import { createSlice, SerializedError } from '@reduxjs/toolkit';

import { CustomerSearchRow, SearchSeriesData, SeriesIndexDocument, SeriesWithEvents } from './types';
import { getSeriesById, recordEventOutcome, recordSeriesOutcome, searchCustomers, searchSeries, updateSeries } from './thunks';

type SearchCustomersState = {
  data: CustomerSearchRow[] | null;
  loading: boolean;
  error: SerializedError | null;
  requestId: string | null;
};

type GetSeriesByIdState = {
  data: SeriesWithEvents | null;
  loading: boolean;
  error: SerializedError | null;
  requestId: string | null;
};

type SearchSeriesState = {
  data: SearchSeriesData | null;
  loading: boolean;
  error: SerializedError | null;
  requestId: string | null;
};

type UpdateSeriesState = {
  loading: boolean;
  error: SerializedError | null;
  requestId: string | null;
  savedAt: number | null;
};

// Rising-edge markers for outcome saves — one per thunk. `savedAt` is bumped
// to Date.now() on the corresponding `.fulfilled`. Consumers watch them to
// refetch derived data (e.g. HaOverview list) after an outcome change.
type RecordOutcomeState = {
  savedAt: number | null;
};

type SeriesState = {
  searchCustomers: SearchCustomersState;
  getSeriesById: GetSeriesByIdState;
  searchSeries: SearchSeriesState;
  updateSeries: UpdateSeriesState;
  recordSeriesOutcome: RecordOutcomeState;
  recordEventOutcome: RecordOutcomeState;
};

const initialState: SeriesState = {
  searchCustomers: {
    data: null,
    loading: false,
    error: null,
    requestId: null,
  },
  getSeriesById: {
    data: null,
    loading: false,
    error: null,
    requestId: null,
  },
  searchSeries: {
    data: null,
    loading: false,
    error: null,
    requestId: null,
  },
  updateSeries: {
    loading: false,
    error: null,
    requestId: null,
    savedAt: null,
  },
  recordSeriesOutcome: {
    savedAt: null,
  },
  recordEventOutcome: {
    savedAt: null,
  },
};

const slice = createSlice({
  name: 'series',
  initialState,
  reducers: {
    resetSearchCustomers(state) {
      state.searchCustomers = { ...initialState.searchCustomers };
    },
    resetGetSeriesById(state) {
      state.getSeriesById = { ...initialState.getSeriesById };
    },
    resetSearchSeries(state) {
      state.searchSeries = { ...initialState.searchSeries };
    },
    resetUpdateSeriesNote(state) {
      state.updateSeries = { ...initialState.updateSeries };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchCustomers.pending, (state, action) => {
      state.searchCustomers.loading = true;
      state.searchCustomers.error = null;
      state.searchCustomers.requestId = action.meta.requestId;
    });
    builder.addCase(searchCustomers.fulfilled, (state, action) => {
      if (state.searchCustomers.requestId !== action.meta.requestId) {
        return;
      }
      state.searchCustomers.loading = false;
      state.searchCustomers.error = null;
      state.searchCustomers.data = action.payload;
    });
    builder.addCase(searchCustomers.rejected, (state, action) => {
      if (state.searchCustomers.requestId !== action.meta.requestId) {
        return;
      }
      state.searchCustomers.loading = false;
      state.searchCustomers.error = action.error;
      state.searchCustomers.data = null;
    });

    builder.addCase(getSeriesById.pending, (state, action) => {
      state.getSeriesById.loading = true;
      state.getSeriesById.error = null;
      state.getSeriesById.requestId = action.meta.requestId;
    });
    builder.addCase(getSeriesById.fulfilled, (state, action) => {
      if (state.getSeriesById.requestId !== action.meta.requestId) {
        return;
      }
      state.getSeriesById.loading = false;
      state.getSeriesById.error = null;
      state.getSeriesById.data = action.payload;
    });
    builder.addCase(getSeriesById.rejected, (state, action) => {
      if (state.getSeriesById.requestId !== action.meta.requestId) {
        return;
      }
      state.getSeriesById.loading = false;
      state.getSeriesById.error = action.error;
      state.getSeriesById.data = null;
    });

    builder.addCase(searchSeries.pending, (state, action) => {
      state.searchSeries.loading = true;
      state.searchSeries.error = null;
      state.searchSeries.requestId = action.meta.requestId;
    });
    builder.addCase(searchSeries.fulfilled, (state, action) => {
      if (state.searchSeries.requestId !== action.meta.requestId) {
        return;
      }

      const order: string[] = [];
      const byId: Record<string, SeriesIndexDocument> = {};
      for (let resultsIndex = 0; resultsIndex < action.payload.items.length; resultsIndex += 1) {
        const row = action.payload.items[resultsIndex];
        order.push(row.seriesId);
        byId[row.seriesId] = row;
      }

      state.searchSeries.loading = false;
      state.searchSeries.error = null;
      state.searchSeries.data = { byId, order, archivedCount: action.payload.archivedCount };
    });
    builder.addCase(searchSeries.rejected, (state, action) => {
      if (state.searchSeries.requestId !== action.meta.requestId) {
        return;
      }
      state.searchSeries.loading = false;
      state.searchSeries.error = action.error;
    });

    builder.addCase(updateSeries.pending, (state, action) => {
      state.updateSeries.loading = true;
      state.updateSeries.error = null;
      state.updateSeries.requestId = action.meta.requestId;
      state.updateSeries.savedAt = null;
    });
    builder.addCase(updateSeries.fulfilled, (state, action) => {
      if (state.updateSeries.requestId !== action.meta.requestId) {
        return;
      }

      const cached = state.getSeriesById.data;
      if (cached !== null && cached.series.id === action.payload.id) {
        cached.series = action.payload;
      }

      state.updateSeries.loading = false;
      state.updateSeries.error = null;
      state.updateSeries.savedAt = Date.now();
    });
    builder.addCase(updateSeries.rejected, (state, action) => {
      if (state.updateSeries.requestId !== action.meta.requestId) {
        return;
      }

      state.updateSeries.loading = false;
      state.updateSeries.error = action.error;
      state.updateSeries.savedAt = null;
    });

    builder.addCase(recordSeriesOutcome.fulfilled, (state) => {
      state.recordSeriesOutcome.savedAt = Date.now();
    });
    builder.addCase(recordEventOutcome.fulfilled, (state) => {
      state.recordEventOutcome.savedAt = Date.now();
    });
  },
});

export const {
  resetSearchCustomers,
  resetGetSeriesById,
  resetSearchSeries,
  resetUpdateSeriesNote,
} = slice.actions;

export default slice.reducer;
